import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Redo,
  RestartAlt,
  Save,
  Undo,
  WarningAmberRounded,
} from "@mui/icons-material";

interface PendingEditsBarProps {
  /** Whether the bar is shown (unsaved changes, or a redoable step remains). */
  visible: boolean;
  /** Whether there are actually unsaved changes (vs. only a redoable step). */
  dirty?: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  /** Entries that fail validation and block saving. */
  invalidCount?: number;
  /** Scroll to the first entry needing attention. */
  onReview?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

export const PendingEditsBar: React.FC<PendingEditsBarProps> = ({
  visible,
  dirty = true,
  isSaving,
  onDiscard,
  onSave,
  saveDisabled = false,
  invalidCount = 0,
  onReview,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
}) => {
  const theme = useTheme();
  if (!visible) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "sticky",
        bottom: 16,
        mt: 3,
        p: 2,
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        gap: 2,
        zIndex: 10,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 8px 24px ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.15)"}`,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          color={dirty ? "text.primary" : "text.secondary"}
        >
          {dirty ? "Unsaved changes" : "All changes undone"}
        </Typography>
        {invalidCount > 0 && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <WarningAmberRounded sx={{ fontSize: 14 }} />
            {invalidCount} {invalidCount === 1 ? "entry needs" : "entries need"}{" "}
            attention
            {onReview && (
              <Button
                size="small"
                onClick={onReview}
                sx={{ textTransform: "none", minWidth: 0, ml: 0.5, py: 0 }}
              >
                Review
              </Button>
            )}
          </Typography>
        )}
      </Box>
      <Tooltip title="Undo">
        <span>
          <IconButton
            size="small"
            onClick={onUndo}
            disabled={isSaving || !canUndo}
            aria-label="Undo"
          >
            <Undo fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Redo">
        <span>
          <IconButton
            size="small"
            onClick={onRedo}
            disabled={isSaving || !canRedo}
            aria-label="Redo"
          >
            <Redo fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <Button
        variant="outlined"
        size="small"
        startIcon={<RestartAlt fontSize="small" />}
        onClick={onDiscard}
        disabled={isSaving || !dirty}
        sx={{ textTransform: "none" }}
      >
        Discard
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={
          isSaving ? (
            <CircularProgress size={16} sx={{ color: "inherit" }} />
          ) : (
            <Save fontSize="small" />
          )
        }
        onClick={onSave}
        disabled={isSaving || saveDisabled}
        sx={{ textTransform: "none" }}
      >
        <Box component="span" sx={{ ml: isSaving ? 0.5 : 0 }}>
          Save
        </Box>
      </Button>
    </Paper>
  );
};
